/**
 * field-validation — shared input rules for person/contact fields.
 *
 * Raised by the operator (2026-08-07, Pat): "email address and phone number in
 * staff edit have no verification — it must be ensured the entered values are
 * correct." Before this, the staff editor accepted "asdf" as an email and
 * saved it; the only phone rule in the app lived privately inside
 * `screens/account/AccountDetails.tsx`. One module now owns both rules so the
 * staff editor, the account screen and anything after them cannot disagree
 * about what a valid address or number is.
 *
 * Deliberately NOT a "correctness" claim beyond shape: no MX lookup, no
 * carrier check. The rules are the ones a form can honestly enforce —
 * structure, length, character set — and the copy says so.
 */

/**
 * Pragmatic RFC-5322 subset: one @, a non-empty local part without spaces, a
 * dotted domain with a 2+ letter TLD. Rejects the failures a typist actually
 * makes (missing @, trailing dot, "name@company", double dots, spaces).
 */
const EMAIL_RE = /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z]{2,}$/;

export function isValidEmail(value: string): boolean {
  const v = (value ?? "").trim();
  return v.length > 0 && v.length <= 254 && EMAIL_RE.test(v);
}

/** E.164-ish: optional +, 7–15 digits, with spaces / ( ) / - / . tolerated. */
export function isValidPhone(value: string): boolean {
  const v = (value ?? "").trim();
  if (!v) return true; // empty is "not given", not "wrong" — required-ness is the caller's rule
  if (!/^\+?[\d\s().-]+$/.test(v)) return false;
  const digits = v.replace(/\D/g, "");
  return digits.length >= 7 && digits.length <= 15;
}

/** Collapse a typed number to the stored form: leading +, digits only. */
export function normalizePhone(value: string): string {
  const v = (value ?? "").trim();
  if (!v) return "";
  const plus = v.startsWith("+");
  const digits = v.replace(/\D/g, "");
  return (plus ? "+" : "") + digits;
}

export interface FieldMessages { email: string; phone: string }

/** Default copy, so every caller says the same thing. Pass through t() to localize. */
export const FIELD_ERRORS: FieldMessages = {
  email: "Enter a valid email address — name@company.com.",
  phone: "Enter a valid phone number — 7–15 digits, optional leading +.",
};
